// src/components/ProfileTestPage.js
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import '../utils/axiosConfig'; // runs the 401/403 logout logic globally
import "../Style/Profile.css";
import "../Style/Responsive.css";
import { useNavigate } from 'react-router-dom';
 
// List of African countries
const africanCountries = [
  'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi',
  'Cabo Verde', 'Cameroon', 'Central African Republic', 'Chad', 'Comoros',
  'Congo - Republic', 'Congo - Democratic Republic', "Cote dIvoire", 'Djibouti',
  'Egypt', 'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon',
  'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Kenya', 'Lesotho', 'Liberia',
  'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco',
  'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Rwanda', 'Sao Tome and Principe',
  'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa',
  'South Sudan', 'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Zambia', 'Zimbabwe'
];
 
// Sample business types
const businessTypes = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company',
  'Corporation',
  'Non-Profit',
  'Cooperative',
  'Other',
];
 
// Possible business stages
const businessStages = [
  'Pre-seed',
  'Seed',
  'Series A',
  'Series B',
  'Growth',
  'Maturity',
  'Exit stage'
];
 
// Common sectors array
const possibleSectors = [
  'Agriculture',
  'FinTech',
  'HealthTech',
  'E-commerce',
  'Education',
  'Energy',
  'Transportation',
  'Logistics',
  'Manufacturing',
  'Tourism',
  'Construction',
  'Food & Beverage',
  'Other',
];
 
// Example investment types
const investmentTypes = [
  'Equity',
  'Debt',
  'Convertible Note',
  'Revenue Share',
  'Other'
];
 
// Define the investment target ranges
const investmentTargetRanges = [
  { label: 'Below 10000', min: 0, max: 10000 },
  { label: '10000 - 50000', min: 10000, max: 50000 },
  { label: '50000 - 100000', min: 50000, max: 100000 },
  { label: '100000 - 500000', min: 100000, max: 500000 },
  { label: '500000 - 1000000', min: 500000, max: 1000000 },
  { label: '1000000 - 5000000', min: 1000000, max: 5000000 },
  { label: 'Above 5000000', min: 5000000, max: 10000000 },
];



// Define the revenue target ranges (similar logic to investmentTargetRanges)
/** Here we assume a range in Ghana Cedis that will be converted to USD later in the backend.
const revenueTargetRanges = [
  { label: 'USD 30,000 - USD 100,000', min: 30000, max: 10000 },
];

/**
 * MultiSelectDropdown Component
 */
const MultiSelectDropdown = ({ options, selected, placeholder, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
 
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );
 
  const handleSelect = (value) => {
    const alreadySelected = selected.includes(value);
    let newSelected;
    if (alreadySelected) {
      newSelected = selected.filter(item => item !== value);
    } else {
      newSelected = [...selected, value];
    }
    onChange(newSelected);
  };
 
  const removeTag = (value) => {
    onChange(selected.filter(item => item !== value));
  };
 
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
 
  return (
    <div className="multi-select-container" ref={containerRef}>
      <div className="selected-tags" onClick={toggleDropdown}>
        {selected.length === 0 && (
          <span className="placeholder">{placeholder}</span>
        )}
        {selected.map(item => (
          <span key={item} className="tag">
            {item}
            <button
              type="button"
              className="tag-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(item);
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {isOpen && (
        <div className="dropdown-list">
          <input
            type="text"
            placeholder="Search..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          <div className="options-container">
            {filteredOptions.map(option => {
              const isSelected = selected.includes(option);
              return (
                <div
                  key={option}
                  className={`option-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                  {isSelected && <span className="checkmark"> ✓</span>}
                </div>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="option-item no-results">No matching options</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
 
// Default/empty profile shape. Also used to sanitize API responses so a
// null or missing field from the backend can never reach the controlled
// inputs and crash the render (the cause of the white blank screen).
const INITIAL_PROFILE = {
  // Common fields
  business_name: '',
  business_type: '',
  description: '',
  location: '',
  email: '',
  business_address: '',
  avatar: '',
  annual_turnover: '',
  financial_position: '',
  pitch_deck: [],
  // Investor fields
  investment_preference: '',
  investment_target: null,
  sectors_of_interest: [],
  investment_types_investor: [],
  business_stages_investor: [],
  // Investee fields
  business_stage: '',
  capital_needed: '',
  market: '',
  pitch_link: '',
  sector_type: '',
  investment_type: '',
  // New optional fields for Investee
  registration: false,
  years_of_operation: '',
  team_capacity: '',
  revenue: null,
  ability_to_scale: '',
  ability_to_scale_file: null,
  financial_records: '',
  financial_records_file: null,
};

// Fields the UI always treats as arrays (.map / MultiSelectDropdown).
const ARRAY_FIELDS = [
  'pitch_deck',
  'sectors_of_interest',
  'investment_types_investor',
  'business_stages_investor',
];

// Merge an API response onto the default shape, coercing null/undefined to
// safe defaults so every controlled input stays a string/array (never null).
const normalizeProfile = (data = {}) => {
  const merged = { ...INITIAL_PROFILE };

  Object.keys(data).forEach((key) => {
    const value = data[key];

    if (ARRAY_FIELDS.includes(key)) {
      if (Array.isArray(value)) {
        merged[key] = value;
      } else if (value === null || value === undefined || value === '') {
        merged[key] = [];
      } else {
        merged[key] = [value];
      }
      return;
    }

    // Keep real values (incl. extra fields like investment_target_min/max);
    // drop null/undefined so the default ('' or null) is preserved.
    if (value !== null && value !== undefined) {
      merged[key] = value;
    }
  });

  return merged;
};

/**
 * Profile Component
 */
const Profile = () => {
  const navigate = useNavigate();
  const [isProfileSetup, setIsProfileSetup] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userType, setUserType] = useState('');
  // helpers
const countWords = (s = '') =>
  s.trim() ? s.trim().split(/\s+/).filter(Boolean).length : 0;

// live word-count UI state
const [wordCounts, setWordCounts] = useState({
  description: 0,
  investment_preference: 0,
});

// profile fields validation regexes
// Field-specific patterns with length limits (no apostrophes, commas, periods, ampersands)
const namePattern = /^[A-Za-z0-9 .-]{2,100}$/;    // Business Name: letters/numbers, space, dot, hyphen (NO comma)
const addressPattern = /^[A-Za-z0-9 ,.-]{5,200}$/;  // Address: letters/numbers, space, comma, dot, hyphen
const marketLenOk = v => (v || '').length >= 3 && (v || '').length <= 100;
const urlPattern = /^https?:\/\/.+$/;
// Allowed chars: letters/numbers + space + hyphen only
const textAllowed = /^[A-Za-z0-9 ,.-]*$/;         // letters/numbers, space, comma, dot, hyphen
const hasAlphaNum = /[A-Za-z0-9]/;

    // Limits
 const descWordLimit = 400;
//  const prefWordLimit = 100;
 const PREF_CHAR_LIMIT = 255;// mirror backend

   // Field errors state
  const [fieldErrors, setFieldErrors] = useState({
    business_name: '',
    business_address: '',
    market: '',
    pitch_link: '',
    description: '',
    investment_preference: '',
    investment_type: '' 
  });

  // Real-time validation handlers
// Real-time validation handlers
const handleFieldChange = (field, value) => {
  // update profile
  setProfile(prev => ({ ...prev, [field]: value }));

  // validate
  let error = '';
  const v = value ?? '';

  // Field-specific validation
  if (field === 'business_name' && v && !namePattern.test(v)) {
    error = 'Use letters/numbers, spaces, periods, and - (2–100 chars). Commas are not allowed.';
  }

  if (field === 'business_address' && v && !addressPattern.test(v)) {
    error = 'Use letters/numbers, spaces, commas, periods, and - (5–200 chars).';
  }

  if (field === 'market') {
    if (!marketLenOk(v)) {
      error = 'Market must be 3–100 characters.';
    } else if (!textAllowed.test(v)) {
      error = 'Use letters/numbers, spaces, commas, periods, and -';
    } else if (v && !hasAlphaNum.test(v)) {
      error = 'Please include at least one letter or number.';
    }
  }

  if (field === 'pitch_link' && v && !urlPattern.test(v)) {
    error = 'Must start with http:// or https://';
  }

if (field === 'description') {
  const wc = countWords(v);
  setWordCounts(prev => ({ ...prev, description: wc }));

  if (wc > descWordLimit) {
    error = `Max ${descWordLimit} words allowed, you have ${wc}.`;
  } else if (!textAllowed.test(v)) {
    error = 'Use letters/numbers, spaces, commas, periods, and - only.';
  } else if (v && !hasAlphaNum.test(v)) {
    error = 'Please include at least one letter or number.';
  }
}


if (field === 'investment_preference') {
  const len = v.length;

  if (len > PREF_CHAR_LIMIT) {
    error = `Max ${PREF_CHAR_LIMIT} characters; you have ${len}.`;
  } else if (!textAllowed.test(v)) {
    error = 'Use letters/numbers, spaces, commas, periods, and - only.';
  } else if (v && !hasAlphaNum.test(v)) {
    error = 'Please include at least one letter or number.';
  }
}

  if (field === 'investment_type' && !v) {
  error = 'Please select an investment type.';
}

  // Update field errors state
  setFieldErrors(prev => ({ ...prev, [field]: error }));
};

  // Main profile state (see INITIAL_PROFILE for the full field shape).
  const [profile, setProfile] = useState(INITIAL_PROFILE);
 

  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    // 1. Fetch user data to determine user type
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/user/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((response) => {
        if (response.data) {
          setUserType(response.data.user_type); // 'investor' or 'investee'
        }
      })
      .catch((err) => {
        console.error('Error fetching user type:', err);
        setError('Failed to fetch user data');
      });
 
    // 2. Fetch profile data
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/profile/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((response) => {
        if (response.data) {
          // Sanitize against the default shape so no null/missing field can
          // reach the controlled inputs and crash the render.
          const prof = normalizeProfile(response.data);

          // Process investment target if available (from raw min/max fields).
          const rawMin = response.data.investment_target_min;
          const rawMax = response.data.investment_target_max;
          if ((rawMin ?? null) !== null || (rawMax ?? null) !== null) {
            const minVal = Number(rawMin);
            const maxVal = (rawMax ?? null) === null ? Infinity : Number(rawMax);

            const matchedRange = investmentTargetRanges.find(
              (range) => range.min === minVal && range.max === maxVal
            );

            prof.investment_target = matchedRange || { min: minVal, max: maxVal };
          }

          setProfile(prof);
          setIsProfileSetup(true);
        }
      })
      .catch((err) => {
        console.error('Error fetching profile:', err);
        if (err.response && err.response.status === 404) {
          setIsProfileSetup(false); // new user
        } else {
          setError('Failed to load profile data');
        }
      });
  }, [navigate]);
 
  // Handle multi-select field changes
  const handleMultiSelectChange = (fieldName, newValues) => {
    setProfile({ ...profile, [fieldName]: newValues });
  };
 
  // Handle form submission: transform investment_target and revenue if needed
  const handleSubmit = async (e) => {
    e.preventDefault();
    // final check: ensure no field errors
    const hasError = Object.values(fieldErrors).some(msg => msg);
    if (hasError) return;
  // profile fields validation checks
  if (!namePattern.test(profile.business_name)) {
  return setError('Business name invalid: use letters/numbers, spaces, periods, and - (2–100 chars). Commas are not allowed.');
}
if (profile.business_address && !addressPattern.test(profile.business_address)) {
  return setError('Business address invalid: use letters/numbers, spaces, commas, periods, and - (5–200 chars).');
}
if (profile.market &&
   (!textAllowed.test(profile.market) || !hasAlphaNum.test(profile.market) || !marketLenOk(profile.market))) {
  return setError('Market must use letters/numbers, spaces, commas, periods, and - (3–100 chars) and include at least one letter/number.');
}
if (profile.investment_preference && profile.investment_preference.length > PREF_CHAR_LIMIT) {
  return setError(`Investment Preference must be ≤ ${PREF_CHAR_LIMIT} characters.`);
}

if (profile.description &&
   (!textAllowed.test(profile.description) || !hasAlphaNum.test(profile.description))) {
  return setError('Description must use letters/numbers, spaces, commas, periods, and - and include at least one letter/number.');
}
    if (profile.pitch_link && !urlPattern.test(profile.pitch_link)) {
      return setError('Pitch link must be a valid URL starting with http:// or https://');
    }
    // Clear any previous error
    setError('');
if (userType === 'investee') {
  if (!profile.investment_type) {
    return setError('Please select an Investment Type.');
  }
  if (!profile.sector_type) {
    return setError('Please select a Sector Type.');
  }
  if (!profile.business_stage) {
    return setError('Please select a Business Stage.');
  }
}
    // Ensure user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const payload = { ...profile };
 
  // map your investment_target to min/max fields
if (payload.investment_target) {
  payload.investment_target_min = Number(payload.investment_target.min).toFixed(2);
  payload.investment_target_max =
    payload.investment_target.max === Infinity
      ? null
      : Number(payload.investment_target.max).toFixed(2);
  delete payload.investment_target;
}
    
    // you don’t need to convert turnover unless you want a number
// if you do:
if (profile.annual_turnover) {
  payload.annual_turnover = Number(profile.annual_turnover);
}
payload.financial_position = profile.financial_position;

payload.annual_turnover = Number(profile.annual_turnover);
payload.financial_position = profile.financial_position;

// Process revenue field similarly if a range is selected
    if (profile.revenue) {
      payload.revenue_min = Number(profile.revenue.min).toFixed(2);
      payload.revenue_max = Number(profile.revenue.max).toFixed(2);
      delete payload.revenue;
    }
  // Build FormData
  const formData = new FormData();

Object.entries(payload).forEach(([key, val]) => {
  // skip null/undefined, empty strings, and pitch_deck (handled separately)
  if (val == null || val === '' || key === 'pitch_deck') return;

  // files will be appended explicitly later
  if (val instanceof File) {
    formData.append(key, val);
  } else if (Array.isArray(val) || typeof val === 'object') {
    formData.append(key, JSON.stringify(val));
  } else {
    formData.append(key, val);
  }
});


  try {
    const url    = `${process.env.REACT_APP_API_BASE_URL}/profile/`;
    const config = { headers: { Authorization: `Token ${token}` } };

    if (isProfileSetup) {
      // PATCH preserves existing files when none are re-uploaded
      await axios.put(url, formData, config);
      setSuccess('Profile updated successfully!');
    } else {
      await axios.post(url, formData, config);
      setSuccess('Profile created successfully!');
    }

    setTimeout(() => {
      navigate(userType === 'investor' ? '/investor-page' : '/investee-page');
    }, 2000);

} catch (err) {
  console.error('Error saving profile:', err);

  let msg = 'We could not save your profile. ';
  const res = err?.response;

  if (res) {
    // --- Server errors (5xx)
    if (res.status >= 500) {
      // Common pitfall: missing required select on investee
      if (userType === 'investee' && !profile.investment_type) {
        msg += 'Investment Type is required for investees. Please select one and try again.';
      } else {
        msg += 'Our server hit an unexpected error while saving. If you used special characters (e.g., “&”) in Business Name, remove them and try again.';
      }
    }
    // --- Validation / client errors (e.g., 400/422) – show field-specific error
    else if (res.data) {
      if (typeof res.data === 'object' && !Array.isArray(res.data)) {
        if (res.data.detail) {
          msg += String(res.data.detail);
        } else {
          const entries = Object.entries(res.data);
          if (entries.length) {
            const [field, problems] = entries[0];
            const firstProblem = Array.isArray(problems) ? problems[0] : problems;
            msg += `${field.replace(/_/g, ' ')}: ${String(firstProblem)}`;
          } else {
            msg += 'Please review your entries and try again.';
          }
        }
      } else {
        // string or other payload
        msg += 'Please review your entries and try again.';
      }
    }
    // --- Other HTTP responses without data
    else {
      msg += 'Please review your entries and try again.';
    }
  } else {
    // No HTTP response (network / CORS / timeout)
    if (err?.code === 'ERR_NETWORK') {
      msg += 'Network error. Check your internet connection and try again.';
    } else {
      msg += err?.message || 'Please try again.';
    }
  }

  setError(msg);
}
  };
  return (
    <div className="profile-container">
      <div className="profile-form">
        <h2>{isProfileSetup ? 'Update your profile for matching' : 'Set up your profile'}</h2>
 
        <form onSubmit={handleSubmit} className="profile-form">
          {/* Existing common and investor fields remain unchanged */}
          <div>
            <label className="field-label" htmlFor="business_name">Business Name</label>
            <input
              name="business_name"
              type="text"
              className="input-field"
              maxLength={100}
              pattern="[A-Za-z0-9 .\-]{2,100}"
              title="Use letters/numbers, spaces, periods, and - (2–100 chars). Commas are not allowed."
              value={profile.business_name}
              onChange={e => handleFieldChange('business_name', e.target.value)}
              // onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
              required
              autoComplete="off"
              id="business_name"
            />
             {fieldErrors.business_name && (
              <div className="field-error">{fieldErrors.business_name}</div>
            )}
          </div>
 
          <div>
            <label className="field-label">Business Type</label>
            <select
              value={profile.business_type}
              onChange={(e) => setProfile({ ...profile, business_type: e.target.value })}
              required
              className="input-field"
              autoComplete="off" 
            >
              <option value="">Select a Business Type...</option>
              {businessTypes.map((type, idx) => (
                <option key={idx} value={type}>{type}</option>
              ))}
            </select>
          </div>
 
          <div>
            <label className="field-label">Description</label>
            <textarea
              rows="3"
              value={profile.description}
              onChange={e => handleFieldChange('description', e.target.value)}
              required
              className="input-field"
              autoComplete="off"
              pattern="[A-Za-z0-9 ,.\-]*"
              title="Use letters/numbers, spaces, commas, periods, and -"
            />
            <div className={`help-text ${wordCounts.description > descWordLimit ? 'text-error' : ''}`}>
              {wordCounts.description}/{descWordLimit} words
            </div>
            {fieldErrors.description && (
              <div className="field-error">{fieldErrors.description}</div>
            )}
          </div>

 
          <div>
            <label className="field-label">Location (Country)</label>
            <select
              value={profile.location}
              onChange={(e) => setProfile({ ...profile, location: e.target.value })}
              required
              className="input-field"
              autoComplete="off" 
            >
              <option value="">Select a Country...</option>
              {africanCountries.map((country, idx) => (
                <option key={idx} value={country}>{country}</option>
              ))}
            </select>
          </div>
 
          <div>
            <label className="field-label" htmlFor="business_address">Business Address</label>
            <input
              type="text"
              className="input-field"
              maxLength={200}
              pattern="[A-Za-z0-9 ,.\-]{5,200}"
              title="Use letters/numbers, spaces, commas, periods, and - (5–200 chars)."
              value={profile.business_address}
              autoComplete="off"
              // onChange={(e) => setProfile({ ...profile, business_address: e.target.value })}
               onChange={e => handleFieldChange('business_address', e.target.value)}
               id="business_address"
            />
             {fieldErrors.business_address && (
              <div className="field-error">{fieldErrors.business_address}</div>
            )}
          </div>
 
{userType === 'investor' && (
<>
          <div>
            <label className="field-label">Investment Preference</label>
            <textarea
              rows="2"
              value={profile.investment_preference}
              onChange={e => handleFieldChange('investment_preference', e.target.value)}
              className="input-field"
              autoComplete="off"
              pattern="[A-Za-z0-9 ,.\-]*"
              title="Use letters/numbers, spaces, commas, periods, and -"
            />
            <div
              className={
                (profile.investment_preference || '').length > PREF_CHAR_LIMIT
                  ? 'field-error'
                  : 'help-text'
              }
            >
              {(profile.investment_preference || '').length}/{PREF_CHAR_LIMIT} chars
            </div>

            {fieldErrors.investment_preference && (
              <div className="field-error">{fieldErrors.investment_preference}</div>
            )}
          </div>


              
              <div>
                <label className="field-label">Investment Target (USD)</label>
                <select
                  className="input-field"
                  required
                  value={profile.investment_target ? JSON.stringify(profile.investment_target) : ""}
                  onChange={(e) =>
                    setProfile({ ...profile, investment_target: JSON.parse(e.target.value) })
                  }
                >
                  <option value="">Select an amount range...</option>
                  {investmentTargetRanges.map((range, idx) => (
                    <option key={idx} value={JSON.stringify(range)}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>
 
              <div>
                <label className="field-label">Sectors of Interest</label>
                <MultiSelectDropdown
                  options={possibleSectors}
                  selected={profile.sectors_of_interest}
                  placeholder="Select one or more sectors..."
                  onChange={(values) => handleMultiSelectChange('sectors_of_interest', values)}
                />
              </div>
 
              <div>
                <label className="field-label">Investment Type (Select multiple)</label>
                <MultiSelectDropdown
                  options={investmentTypes}
                  selected={profile.investment_types_investor}
                  placeholder="Select one or more types..."
                  onChange={(values) => handleMultiSelectChange('investment_types_investor', values)}
                />
              </div>
 
              <div>
                <label className="field-label">
                  Which business stage do you want to invest in? (Select multiple)
                </label>
                <MultiSelectDropdown
                  options={businessStages}
                  selected={profile.business_stages_investor}
                  placeholder="Select one or more stages..."
                  onChange={(values) => handleMultiSelectChange('business_stages_investor', values)}
                />
              </div>
            </>
          )}
 
 {userType === 'investee' && (
  <>

           <div>
                <label className="field-label">Business Stage</label>
                <select
                  value={profile.business_stage}
                  onChange={(e) => setProfile({ ...profile, business_stage: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Select Business Stage...</option>
                  {businessStages.map((stage, idx) => (
                    <option key={idx} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>
 
              <div>
                <label className="field-label">Ticket Size (USD)</label>
                <input
                  type="number"
                  value={profile.capital_needed}
                  onChange={(e) => setProfile({ ...profile, capital_needed: e.target.value })}
                  className="input-field"
                  autoComplete="off" 
                />
              </div>

              <div>
                <label className="field-label">Team Capacity</label>
                <input
                  type="number"
                  value={profile.team_capacity}
                  onChange={(e) => setProfile({ ...profile, team_capacity: e.target.value })}
                  className="input-field"
                  autoComplete="off" 
                />
              </div>
 
              <div>
                <label className="field-label">Sector Type</label>
                <select
                  value={profile.sector_type}
                  onChange={(e) => setProfile({ ...profile, sector_type: e.target.value })}
                  className="input-field"
                  autoComplete="off" 
                  required
                >
                  <option value="">Select a Sector...</option>
                  {possibleSectors.map((sector, idx) => (
                    <option key={idx} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>


{/* Investee — Investment Type */}
<label className="field-label">Investment Type</label>
<select
  required                      // ⬅ block submit if empty
  value={profile.investment_type}
  onChange={(e) => setProfile({ ...profile, investment_type: e.target.value })}
  className="input-field"
>
  <option value="">Select Investment Type...</option>
  {investmentTypes.map((type, idx) => (
    <option key={idx} value={type}>{type}</option>
  ))}
</select>
{fieldErrors.investment_type && (
  <div className="field-error">{fieldErrors.investment_type}</div>
)}

  <div>
  <label className="field-label" htmlFor="market">Market</label>
  <input
    type="text"
    name="market"
    className="input-field"
    value={profile.market}
    onChange={e => handleFieldChange('market', e.target.value)}
    placeholder="Describe target (e.g. B2B in East Africa)"
    id="market"
    autoComplete="off"
    // HTML constraints to match description/investment preference behavior
    pattern="[A-Za-z0-9 ,.\-]*"
    title="Use letters/numbers, spaces, commas, periods, and -"
    minLength={3}
    maxLength={100}
  />
  {fieldErrors.market && (
    <div className="field-error">{fieldErrors.market}</div>
  )}
</div>

         <div>
      <label className="field-label">Annual Turnover (USD)</label>
      <input
        type="number"
        min="0"
        step="1"
        value={profile.annual_turnover}
        onChange={e =>
          setProfile({ ...profile, annual_turnover: e.target.value })
        }
        placeholder="e.g. 125000"
        className="input-field"
        autoComplete="off" 
        // no 'required' here
      />
    </div>

    <div>
      <label className="field-label">Current Financial Position</label>
      <select
        className="input-field"
        value={profile.financial_position}
        onChange={e =>
          setProfile({ ...profile, financial_position: e.target.value })
        }
        // no 'required' here
      >
        <option value="">Select…</option>
        <option value="profit">Profit</option>
        <option value="loss">Loss</option>
        <option value="break-even">Break-even</option>
      </select>
    </div>
         

{/* Pitch Video Link*/} 
              <div>
                <label className="field-label" htmlFor="pitch_link">Pitch Video Link</label>
                <input
                  name="pitch_link"
                  type="url"
                  className="input-field"
                  pattern="https?://.+"
                  title="Must start with http:// or https://"
                  value={profile.pitch_link}
                  // onChange={(e) => setProfile({ ...profile, pitch_link: e.target.value })}
                   onChange={e => handleFieldChange('pitch_link', e.target.value)}
                  placeholder="e.g. https://youtube.com/your-pitch"
                  id="pitch_link"
                  autoComplete="off" 
                />
                {fieldErrors.pitch_link && (
              <div className="field-error">{fieldErrors.pitch_link}</div>
            )}
              </div>
            
  </>
)}
 
              
          <button type="submit" className="match-button">
            {isProfileSetup ? 'Save Changes' : 'Update Profile'}
          </button>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </form>
      </div>
    </div>
  );
};
 
export default Profile;
